import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateListing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    breed: '',
    description: '',
    cost: '',
    location: '',
    contact: '',
    weight: '',
    age: '',
    health_info: '',
    vaccination_details: ''
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        toast.error('Please select a valid image file');
      }
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!imageFile) {
      toast.error('Please select an image');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('image', imageFile);
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/listings`, formDataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Listing created successfully!');
      navigate('/my-listings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4" data-testid="create-listing-page">
      <div className="max-w-3xl mx-auto">
        <Card className="border-neutral-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Young Serif, serif' }}>
              Create New Listing
            </CardTitle>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div>
                <Label className="text-sm font-medium text-neutral-700 mb-2 block">
                  Chicken Image *
                </Label>
                {imagePreview ? (
                  <div className="relative image-preview-container">
                    <img src={imagePreview} alt="Preview" className="rounded-lg" />
                    <Button
                      type="button"
                      onClick={handleRemoveImage}
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 rounded-full"
                      data-testid="remove-image-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-neutral-300 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors bg-muted/30">
                    <Upload className="w-12 h-12 text-neutral-400 mb-2" />
                    <span className="text-neutral-600">Click to upload image</span>
                    <span className="text-sm text-neutral-500 mt-1">PNG, JPG up to 10MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      data-testid="image-input"
                    />
                  </label>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title" className="text-sm font-medium text-neutral-700">
                    Listing Title *
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    data-testid="title-input"
                    className="mt-1 h-12 rounded-lg"
                    placeholder="e.g., Healthy Country Rooster"
                  />
                </div>

                <div>
                  <Label htmlFor="breed" className="text-sm font-medium text-neutral-700">
                    Breed *
                  </Label>
                  <Input
                    id="breed"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    required
                    data-testid="breed-input"
                    className="mt-1 h-12 rounded-lg"
                    placeholder="e.g., Kadaknath"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium text-neutral-700">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  data-testid="description-input"
                  className="mt-1 rounded-lg"
                  rows={4}
                  placeholder="Describe your chickens..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cost" className="text-sm font-medium text-neutral-700">
                    Price (₹) *
                  </Label>
                  <Input
                    id="cost"
                    name="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={handleChange}
                    required
                    data-testid="cost-input"
                    className="mt-1 h-12 rounded-lg"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="location" className="text-sm font-medium text-neutral-700">
                    Location *
                  </Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    data-testid="location-input"
                    className="mt-1 h-12 rounded-lg"
                    placeholder="City, State"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact" className="text-sm font-medium text-neutral-700">
                  Contact Number *
                </Label>
                <Input
                  id="contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  required
                  data-testid="contact-input"
                  className="mt-1 h-12 rounded-lg"
                  placeholder="+91 XXXXXXXXXX"
                />
              </div>

              {/* Additional Details */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
                  Additional Details (Optional)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="weight" className="text-sm font-medium text-neutral-700">
                      Weight
                    </Label>
                    <Input
                      id="weight"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      data-testid="weight-input"
                      className="mt-1 h-12 rounded-lg"
                      placeholder="e.g., 2.5 kg"
                    />
                  </div>

                  <div>
                    <Label htmlFor="age" className="text-sm font-medium text-neutral-700">
                      Age
                    </Label>
                    <Input
                      id="age"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      data-testid="age-input"
                      className="mt-1 h-12 rounded-lg"
                      placeholder="e.g., 6 months"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="health_info" className="text-sm font-medium text-neutral-700">
                    Health Information
                  </Label>
                  <Textarea
                    id="health_info"
                    name="health_info"
                    value={formData.health_info}
                    onChange={handleChange}
                    data-testid="health-info-input"
                    className="mt-1 rounded-lg"
                    rows={3}
                    placeholder="Any health details..."
                  />
                </div>

                <div className="mt-4">
                  <Label htmlFor="vaccination_details" className="text-sm font-medium text-neutral-700">
                    Vaccination Details
                  </Label>
                  <Textarea
                    id="vaccination_details"
                    name="vaccination_details"
                    value={formData.vaccination_details}
                    onChange={handleChange}
                    data-testid="vaccination-input"
                    className="mt-1 rounded-lg"
                    rows={3}
                    placeholder="Vaccination history..."
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                data-testid="submit-listing-btn"
                className="w-full bg-primary hover:bg-primary/90 text-white h-12 rounded-full text-base font-medium"
              >
                {loading ? 'Creating Listing...' : 'Create Listing'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateListing;